import {
  Box,
  Button,
  Center,
  Collapse,
  Flex,
  HStack,
  Progress,
  Spacer,
  Text,
  Tooltip,
  VStack,
  UnorderedList,
  ListItem,
  useBoolean,
  useMediaQuery,
  Modal,
  ModalOverlay,
  ModalBody,
  ModalContent,
  ModalHeader,
  Input,
  ModalCloseButton,
  Spinner,
} from '@chakra-ui/react';
import { motion, AnimatePresence } from "framer-motion"
import { CloseIcon, WarningIcon, InfoIcon } from '@chakra-ui/icons';
import { Tabs, TabList, TabPanels, Tab, TabPanel } from '@chakra-ui/react';
import PerfectScrollbar from 'react-perfect-scrollbar';
import React, { useMemo, useContext, useEffect, useState, useRef } from 'react';
import Select from 'react-select';
import CQContainer from '../../components/layout/CQContainer';
import { FONT_FAMILY } from '../../constants/Theme';
import { useDropzone } from 'react-dropzone';
import { acceptStyle, baseStyle, focusedStyle, rejectStyle } from './styles';
import {
  CrossIcon,
  GoogleDriveIcon,
  MsOneDriveIcon,
  PinIcon,
  RedCrossIcon,
  TickIcon,
  UploadIcon,
} from '../../constants/IconData';
import AppColors from '../../constants/AppColors';
import { withProvider } from '../../hoc/withProvider';
import UploadProvider, { UploadContext } from '../../providers/UploadProvider';
import { AuthContext } from '../../providers/AuthProvider';
import useDeviceScreen from '../../hooks/useDeviceScreen';
import GoogleDriveUploader from './components/google_drive_uploader';
import OneDriveUpload from './components/one_drive_uploader';
import uploadRepository from '../../repositories/UploadRepository';
import { BULK_DELETE_CHART_UPLOADS } from '../../constants/Endpoints';
import { useLocation } from 'react-router-dom';
import { getCurrentUserTypeIdObject } from '../../utils/getCurrentUserTypeDetails';
import TemplateDownloadBanner from './components/TemplateDownloadBanner';
import useTemplateValidation, { isExcelOrCsv } from "./useTemplateValidation";
import templateFile from "../../assets/Template.xlsx";
import { WarningText } from '../../constants/IconData';
import { ClientType } from '../../utils/clientTypeUtils';

const TEMPLATE_DOWNLOAD_URL = templateFile;

// ---------------------------------------------------------------------------
// Standalone banners — defined at module level so they can receive props
// ---------------------------------------------------------------------------

const SoftBlockBanner = ({ softWarnFiles }) => {
  if (!softWarnFiles.length) return null;
  return (
    <Box mt={3} p={3} bg="orange.50" border="1.5px solid" borderColor="orange.300" borderRadius="10px">
      <HStack align="flex-start" spacing={2}>
        <InfoIcon color="orange.500" mt="2px" flexShrink={0} />
        <Box>
          <Text fontSize="13px" color="orange.700" fontWeight="semibold">
            Please use our template for best results.{" "}
            <Box as="a" href={TEMPLATE_DOWNLOAD_URL} download="Template.xlsx" color="orange.800" fontWeight="bold" textDecoration="underline">
              Download template
            </Box>
          </Text>
          {softWarnFiles.map((name) => (
            <Text key={name} fontSize="11px" color="orange.600" fontFamily="mono" mt={1}>
              {name}
            </Text>
          ))}
        </Box>
      </HStack>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// DropdownComponent — clean, no banners inside
// ---------------------------------------------------------------------------

const DropdownComponent = ({
  options,
  setSelected,
  selected,
  placeholder = "Select Options",
  isDisabled = false,
  disabledText = "Dropdown disabled",
  isLoading = false
}) => {
  const handleChange = (option) => setSelected(option);

  return (
    <div style={{ flex: 1 }}>
        {isDisabled ? (
          <Tooltip
            placement="bottom"
            hasArrow
            label={disabledText}
          >
            <div style={{ width: "100%" }}>
              <Select
                styles={{
                  container: (base) => ({
                    ...base,
                    width: "100%",
                  }),
                }}
                options={options}
                onChange={handleChange}
                isClearable
                placeholder={placeholder}
                isDisabled={isDisabled}
                value={selected}
                isLoading={isLoading}
              />
            </div>
          </Tooltip>
        ) : (
          <Select
            styles={{
              container: (base) => ({
                ...base,
                width: "100%",
              }),
            }}
            options={options}
            onChange={handleChange}
            isClearable
            placeholder={placeholder}
            isDisabled={isDisabled}
            value={selected}
            isLoading={isLoading}
          />
        )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// SubmitModal — unchanged
// ---------------------------------------------------------------------------

const SubmitModal = ({ isOpen, onClose, onSubmit }) => {
  const {
    refreshSubmitModalMetadataRef,
    uploadedCharts,
    validateBatchName,
    isvalidatingBatchName,
    isSubmitting,
    clientProviderTree,
    isFetchingClientProviderTree,
    specialtyProviderTree,
    setSpecialtyProviderTree,
    providerTree,
    setProviderTree
  } = useContext(UploadContext);

  const authContext = useContext(AuthContext)
  const [selectedHospital, setSelectedHospital] = useState(null);
  const [selectedSpecialty, setSelectedSpecialty] = useState(null);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [isNotSmallScren] = useMediaQuery('(min-width: 410px)');
  const [batchNotes, setBatchNotes] = useState(null);
  const [batchName, setBatchName] = useState("");
  const allowedBatchNameRegex = useMemo(() => /^[a-zA-Z0-9-_]*$/, []);
  const disallowedRegex = useMemo(() => /[^a-zA-Z0-9-_]+/g, []);
  const [batchNameWarning, setBatchNameWarning] = useState("");
  const [isBatchNameVerified, setIsBatchNameVerified] = useState(null);
  const [suggestedBatchName, setSuggestedBatchName] = useState(null);

  useEffect(() => {
    refreshSubmitModalMetadataRef.current = () => {
      if (authContext?.currentUser?.user_type === ClientType.HOSPITAL || 
        authContext?.currentUser?.user_type === ClientType.PHYSICIANS_GROUP
      ) {
        setSelectedHospital({ value: authContext?.currentUser?.hospital_id?.id });
      } else {
        setSelectedHospital(null);
      }
      if (authContext?.currentUser?.user_type === ClientType.DEPARTMENT) {
        setSelectedSpecialty({ value: authContext?.currentUser?.department_id?.specialty__id})
      } else {
        setSelectedSpecialty(null);
      }
      setSelectedProvider(null);
      setBatchNotes(null);
      setBatchName("");
      setSuggestedBatchName(null);
    };
  }, []);


  useEffect(() => {
    if (!batchName) {
      setIsBatchNameVerified(true);
    } else {
      setIsBatchNameVerified(null);
    }
  }, [batchName]);

  const removeExtension = (filename) => {
    if (!filename.includes('.') || filename.startsWith('.')) return filename;
    return filename.split('.').slice(0, -1).join('.');
  };

  useEffect(() => {
    if (!isOpen) return;
    let prefix = null;
    for (const chart of uploadedCharts) {
      if (!chart.name) continue;
      const nameWithoutExtension = removeExtension(chart.name);
      if (!prefix) { prefix = nameWithoutExtension; continue; }
      let maxCommon = "";
      for (let idx = 0; idx < Math.min(nameWithoutExtension.length, prefix.length); idx++) {
        if (nameWithoutExtension[idx].toLowerCase() !== prefix[idx].toLowerCase()) break;
        maxCommon += nameWithoutExtension[idx];
      }
      prefix = maxCommon;
    }
    if (prefix) {
      prefix = prefix.replace(disallowedRegex, "_");
      prefix = prefix.replace(/_+$/, "")
      handleBatchNameVerification(prefix, true).then(res => 
        setSuggestedBatchName(res)
      );
    }
  }, [isOpen]);

  const onBatchNameChange = (e) => {
    const val = e.target.value;
    if (allowedBatchNameRegex.test(val)) {
      setBatchName(val);
      setBatchNameWarning("");
    } else {
      setBatchNameWarning("Only A-Z , a-z , 0-9 , - , _ allowed")
    }
  };

  const handleBatchNameVerification = async (argBatchName = null, suggestion = false) => {
    const res = await validateBatchName(argBatchName || batchName, suggestion);
    if ("agree" in res) {
      setIsBatchNameVerified(true);
      setSuggestedBatchName(null);
      return res["agree"];
    }
    if ("suggestion" in res) {
      if (!suggestion) setIsBatchNameVerified(false);
      setSuggestedBatchName(res["suggestion"]);
      return res["suggestion"];
    }
  };

  return (
    <>
      <Modal
      isOpen={isOpen}
      size={isNotSmallScren ? 'xl' : 'lg'}
      onClose={onClose}
      isCentered
      >
        <ModalOverlay style={{ backgroundColor: AppColors.lightSkyBlue }} />
          <ModalContent
          p={15}
          alignItems={'center'}
          >
            <ModalHeader>
              <Center fontSize={'xl'}
                  color={AppColors.secondary}
                  fontFamily={FONT_FAMILY.baiJamurjee}
                  fontWeight='bold'>
                  Additional Information
              </Center>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody mb={2} width={"100%"}>
              <Flex
                alignItems={'center'}
                direction={'column'}
              >
                {(authContext?.currentUser?.user_type === ClientType.HEALTH_SYSTEM || 
                authContext?.currentUser?.user_type === ClientType.PROVIDER) && (
                  <Box w="100%" alignSelf={'flex-start'}>
                    <DropdownComponent 
                    options={clientProviderTree?.order?.map(id => ({value: id, label: `${clientProviderTree.hospitals[id].name} - ${clientProviderTree.hospitals[id].state}`}))}
                    setSelected={updatedHospital => {
                      setSelectedHospital(updatedHospital);
                      if (!updatedHospital || updatedHospital.value !== selectedHospital?.value) {
                        setSelectedSpecialty(null);
                        setSelectedProvider(null);
                      }
                      if (updatedHospital) {
                        setSpecialtyProviderTree(clientProviderTree.hospitals[updatedHospital.value])
                      } 
                    }}
                    placeholder={"Select Location (Hospital)"}
                    selected={selectedHospital}
                    isLoading={isFetchingClientProviderTree}
                    />
                </Box>
                )}
                
                
                <HStack spacing={4} width={"100%"} flexShrink={0} marginTop={3}>
                  {authContext?.currentUser?.user_type != ClientType.DEPARTMENT && (
                    <DropdownComponent 
                    options={specialtyProviderTree?.order?.map(id => ({value: id, label: specialtyProviderTree.specialties[id].name}))}
                    setSelected={updatedSpecialty => {
                      setSelectedSpecialty(updatedSpecialty)
                      if (!updatedSpecialty || updatedSpecialty.value !== selectedSpecialty?.value) {
                        setSelectedProvider(null);
                      }
                      if (updatedSpecialty) {
                        setProviderTree(specialtyProviderTree.specialties[updatedSpecialty.value])
                      }
                    }}
                    isDisabled={selectedHospital == null}
                    disabledText='Select Hospital first'
                    placeholder={"Select Specialty (Optional)"}
                    selected={selectedSpecialty}
                    isLoading={isFetchingClientProviderTree}
                    />
                    )
                  }
                  
                  <DropdownComponent 
                  options={providerTree?.order?.map(id => ({value: id, label: providerTree.providers[id].name}))}
                  setSelected={setSelectedProvider}
                  isDisabled={selectedSpecialty == null}
                  disabledText='Select specialty first'
                  placeholder={"Select Provider (Optional)"}
                  selected={selectedProvider}
                  />
                  
                </HStack>

                <Input 
                marginTop={3}
                type='text'
                placeholder='Enter Batch Notes (Optional)'
                value={batchNotes} 
                onChange={(e) => setBatchNotes(e.target.value)} 
                />

                <HStack marginTop={3} width={"100%"} align="flex-start">
                <VStack width={"70%"} marginRight={3} align={'flex-start'}>
                  <Input
                    type="text"
                    value={batchName}
                    onChange={onBatchNameChange}
                    placeholder="Unique Batch Name (Optional)"    
                    // width={"80%"}
                  />
                  <WarningText
                  message={batchNameWarning} />
                  {suggestedBatchName && (
                    <Text
                    as="span"
                    color="blue.500"
                    cursor="pointer"
                    // fontWeight="medium"
                    marginLeft={1}
                    onClick={() => {
                      setBatchNameWarning("");
                      setBatchName(suggestedBatchName)
                    }}
                  >
                    Suggestion: {suggestedBatchName}
                  </Text>
                  )}
                  
                </VStack>
                {(!isvalidatingBatchName && isBatchNameVerified !== null) ? (
                  isBatchNameVerified ? 
                  <span style={{ color: "green", marginTop: 8, fontSize: "20px"}}>✔</span> : 
                  (
                  <span style={{ color: "red", marginTop: 8, fontSize: "20px"}}>✖</span>
                )
                ) : (
                  <Button
                  isLoading={isvalidatingBatchName}
                  onClick={() => handleBatchNameVerification()}>
                    Verify
                  </Button>
                )}
                
                </HStack>

                <Button
                mt={5}
                isLoading={isSubmitting}
                borderRadius={25}
                color={AppColors.white}
                disabled={!isBatchNameVerified || isvalidatingBatchName}
                paddingInline={'35px'}
                backgroundColor={AppColors.primary}
                fontWeight='normal'
                fontSize={'sm'}
                onClick={() => {
                  onSubmit(selectedHospital?.value, selectedSpecialty?.value, selectedProvider?.value, batchNotes, batchName)}}
                > 
                  Submit Charts
                </Button>
                
              </Flex>
            </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

// ---------------------------------------------------------------------------
// UploadContainer
// ---------------------------------------------------------------------------

const UploadContainer = props => {
  const {
    isSubmitting,
    uploadedCharts,
    setUploadedCharts,
    uploadCharts,
    deleteChart,
    bulkDeleteChartUploads,
    submitUploadedCharts,
    isGrouped,
    igState,
    uploadedChartsRef,
  } = useContext(UploadContext);

  const location = useLocation();
  const [isOpen, ioState] = useBoolean(false);

  // ---- template validation hook -------------------------------------------
  // NOTE: for this to work each chart object needs a `file` property (raw File).
  // In your UploadProvider's uploadCharts(), add `file: rawFile` when building
  // the chart object before pushing to uploadedCharts state.
  const { validateFiles, removeFile, clearAll, showSoftWarning, softWarnFiles, syncWithUploaded  } = useTemplateValidation();

  const removeChartsonMovingAway = () => {
    if (!uploadedChartsRef.current || !uploadedChartsRef.current.length) return;
    const payload = JSON.stringify({ 'chart_upload_ids': uploadedChartsRef.current.map(chart => chart.id) });
    if (!!navigator.brave) {
      fetch(BULK_DELETE_CHART_UPLOADS, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
        keepalive: true
      });
    } else {
      const blob = new Blob([payload], { type: "application/json" });
      navigator.sendBeacon(BULK_DELETE_CHART_UPLOADS, blob);
    }
  };

  useEffect(() => {
    const handleBeforeUnload = () => removeChartsonMovingAway();
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  useEffect(() => {
    syncWithUploaded(uploadedCharts.map((c) => c.name));
  }, [uploadedCharts]);

  useEffect(() => {
    return () => removeChartsonMovingAway();
  }, [location]);

  const authContext = useContext(AuthContext);
  const canUpload = authContext.currentUser?.can_upload;

  const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject } = useDropzone({
    accept: { 'text/doc': ['.doc', '.docx', '.pdf', '.xls', '.xlsx', '.hl7'] },
    useFsAccessApi: false,
    onDrop: async acceptedFiles => {
        validateFiles(acceptedFiles);
        await uploadCharts(acceptedFiles, 0);
      },
    disabled: !canUpload,
  });

  const submitCharts = async (selectedHospitalId = null, selectedSpecialtyId, selectedProviderId, batchNotes, batchName) => {
    if (isGrouped) {
      await submitUploadedCharts(
        Object.entries(groupedUploadedCharts)
          .map(([_, charts]) => charts.filter(chart => !chart.cancelled).map(chart => chart.id))
          .filter((value) => value.length > 0)
          .map((val, idx) => groupedStatusList[idx] ? [val] : val)
          .flat(),
        selectedHospitalId,
        selectedSpecialtyId, 
        selectedProviderId, 
        batchNotes, 
        batchName
      );
    } else {
      await submitUploadedCharts(null, selectedHospitalId, selectedSpecialtyId, selectedProviderId, batchNotes, batchName);
    }
  };

  const style = useMemo(() => ({
    ...baseStyle,
    ...(isFocused ? focusedStyle : {}),
    ...(isDragAccept ? acceptStyle : {}),
    ...(isDragReject ? rejectStyle : {}),
  }), [isFocused, isDragAccept, isDragReject]);

  const handleRemoveFile = async id => { await deleteChart(id); };

  const handleRemoveGroupChartUploads = async chartUploadIds => {
    uploadRepository.bulkDeleteChartUpload({ chart_upload_ids: chartUploadIds });
    setUploadedCharts(uploadedCharts.filter(chart => !chartUploadIds.includes(chart.id)));
  };

  const removeGroupStateMetadata = groupId => {
    setIndexOpenStatus(prev => prev.filter((_, index) => index != groupId));
    setGroupedStatusList(prev => prev.filter((_, index) => index != groupId));
  };

  const { isWeb } = useDeviceScreen();

  const allChartsUploaded = useMemo(() => {
    for (let chart of uploadedCharts) { if (chart.value < 100) return false; }
    return true;
  }, [uploadedCharts]);

  const handleRemoveState = (chart_id, groupId, key) => {
    if (isGrouped && groupedUploadedCharts[key].length === 1) {
      removeGroupStateMetadata(groupId);
    }
    setUploadedCharts(prev => prev.filter(chart => chart.id !== chart_id));
  };

  const [groupedUploadedCharts, setGroupedUploadedCharts] = useState({});
  const [indexOpenStatus, setIndexOpenStatus] = useState(null);
  const [groupedStatusList, setGroupedStatusList] = useState(null);

  const toggle = (index) => {
    setIndexOpenStatus(prev => {
      let newStatus = [...prev];
      newStatus[index] = !prev[index];
      return newStatus;
    });
  };

  useEffect(() => {
    if (isGrouped) {
      const statusArray = Array(uploadedCharts.length).fill(false);
      setIndexOpenStatus(statusArray);
      setGroupedStatusList(statusArray.map(val => !val));
    }
  }, [isGrouped]);

  const groupChartsByPrefix = () => {
    setGroupedUploadedCharts(() => {
      let groupedCharts = {};
      for (let item of uploadedCharts) {
        const prefix = item.name.split("_")[0];
        if (!(prefix in groupedCharts)) groupedCharts[prefix] = [];
        groupedCharts[prefix].push(item);
      }
      return groupedCharts;
    });
  };

  useEffect(() => {
    if (isGrouped) groupChartsByPrefix();
  }, [uploadedCharts, isGrouped]);

  const ChartDisplayComponent = (chart, groupId, key = "") => {
    return (
      <motion.div
        key={chart.id}
        layout
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0, x: 50, height: 0 }}
        transition={{ duration: 0.6 }}
        style={{ width: "100%", padding: 1, overflow: "hidden" }}
      >
        <VStack _hover={{ bg: "gray.100" }} align="start" width={"100%"} mb={2} key={chart.id}>
          <HStack width={"100%"}>
            {chart.uploaded && (<span style={{ color: "green" }}>&#10004;</span>)}
            <Text maxWidth="70%" whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden" fontWeight="semibold" fontFamily="mono">
              {chart.name}
            </Text>
            <Spacer />
            <Text>{chart.size}</Text>
            {chart.uploaded && (
              <>
                <CloseIcon
                  boxSize={7} color="red" borderRadius="full" p={1} cursor="pointer"
                  _hover={{ bg: "red.500", color: "white" }}
                  onClick={() => { handleRemoveFile(chart.id); handleRemoveState(chart.id, groupId, key); }}
                />
                {chart.name.length > 200 && <WarningIcon color="red.500" boxSize={5} />}
              </>
            )}
          </HStack>
          <Progress value={chart.value ?? 100} size="xs" colorScheme={chart.cancelled ? 'red' : 'teal'} width="100%" />
        </VStack>
      </motion.div>
    );
  };

  const validateFileNameLength = () => uploadedCharts.some((chart) => chart.name.length > 200);

  const groupingTooltipButtonComponent = () => (
    <Tooltip
      hasArrow placement="left" bg="white" color="gray.800"
      border="1px solid" borderColor="gray.200" borderRadius="md"
      boxShadow="xl" p={4} maxW="400px"
      label={
        !isGrouped ? (
          <Box textAlign="left">
            <Box fontWeight="semibold" mb={2}>Group Files by Prefix</Box>
            <Box fontSize="sm" mb={2}>Files are grouped using the part of the file name before the first underscore (<b>_</b>).</Box>
            <Box fontSize="sm" mb={2}>All files belonging to the same group will be under a single upload(Audit Sheet) (<b>_</b>).</Box>
            <Box fontSize="sm" fontWeight="medium" mb={1}>Example:</Box>
            <UnorderedList fontSize="sm" pl={4} mb={2} fontFamily="mono">
              <ListItem>HospCardio_claim.pdf</ListItem>
              <ListItem>HospCardio_report.pdf</ListItem>
              <ListItem>HospCardio_extra.pdf</ListItem>
              <ListItem>HospNeuro_claim.pdf</ListItem>
              <ListItem>HospNeuro_report.pdf</ListItem>
              <ListItem>HospGastro_claim.pdf</ListItem>
            </UnorderedList>
            <Box fontSize="sm" mt={2}>Groups:</Box>
            <UnorderedList fontSize="sm" pl={4} mb={2} fontFamily="mono">
              <ListItem>HospCardio = claim, report, extra (3 files)</ListItem>
              <ListItem>HospNeuro  = claim, report (2 files)</ListItem>
              <ListItem>HospGastro = claim (1 file)</ListItem>
            </UnorderedList>
          </Box>
        ) : (
          <Box textAlign="left">
            <Box fontWeight="semibold" mb={2}>Ungroup All Files by Prefix</Box>
            <Box fontSize="sm" mb={2}>All the uploaded files that were grouped are ungrouped into individual files. All the uploads are treated as separate uploads (Audit sheet) (<b>_</b>).</Box>
          </Box>
        )
      }
    >
      <Button isLoading={!allChartsUploaded} onClick={igState.toggle}>
        {isGrouped ? "Ungroup all files" : "Group all files"}
      </Button>
    </Tooltip>
  );

  return (
    <>
      <CQContainer>
        <Box>
          <Center>
            <Text fontSize={'30px'} fontFamily={FONT_FAMILY.baiJamurjee} fontWeight={600} color={AppColors.darkBlue}>
              Upload Charts
            </Text>
          </Center>

          <Center>
            <Flex
              borderRadius={12}
              boxShadow="0 4px 16px rgba(0, 0, 0, 0.1)"
              width={isWeb ? '60%' : '100%'}
              mt={5}
              bgColor={'#F8FAFC'}
              flexDirection={'column'}
              p={6}
            >
              {canUpload ? (
                <>
                  <Tabs variant="enclosed-colored" isFitted>
                    <TabList mb="1em">
                      <Tab _selected={{ bg: '#48BB78', color: 'white' }}>
                        <UploadIcon style={{ marginRight: 5, width: 30, height: 30 }} /> Local
                      </Tab>
                      <Tab _selected={{ bg: '#48BB78', color: 'white' }}>
                        <GoogleDriveIcon style={{ marginRight: 5, width: 30, height: 30 }} /> Google Drive
                      </Tab>
                      <Tab _selected={{ bg: '#48BB78', color: 'white' }}>
                        <MsOneDriveIcon style={{ marginRight: 5, width: 30, height: 30 }} /> OneDrive
                      </Tab>
                    </TabList>

                    <TabPanels>
                      {/* Local Tab */}
                      <TabPanel>
                        <TemplateDownloadBanner />
                        <Flex
                          p={4} border="2px dashed" borderColor="#A0AEC0" borderRadius="12px"
                          bgColor={'white'} flexDirection="column" justifyContent="center"
                          _hover={{ borderColor: '#48BB78', bgColor: '#E6FFFA' }}
                          style={{ cursor: 'pointer' }} mb={4}
                        >
                          <Text fontSize="16px" fontWeight="bold" color="#2D3748" mb={4} textAlign="center">
                            Upload from Local
                          </Text>
                          <Flex {...getRootProps({})} flexDirection="column" alignItems="center">
                            <input {...getInputProps()} />
                            <UploadIcon style={{ width: 30, height: 30, color: '#48BB78' }} />
                            <Text fontSize="14px" color="#2D3748" mt={4}>Drag and drop files here</Text>
                            <Text fontSize="12px" color="#A0AEC0">Supported formats: XLS, XLSX, PDF, DOC, DOCX, HL7</Text>
                          </Flex>
                        </Flex>
                      </TabPanel>

                      {/* Google Drive Tab */}
                      <TabPanel>
                        <TemplateDownloadBanner />
                        <GoogleDriveUploader uploadCharts={uploadCharts}>
                          <Text fontSize="16px" fontWeight="bold" color="#2D3748" mb={4}>Upload from Google Drive</Text>
                          <GoogleDriveIcon />
                          <Text fontSize="12px" color="#A0AEC0" mt={4}>Supported formats: XLS, XLSX, PDF, DOC, DOCX, HL7</Text>
                        </GoogleDriveUploader>
                      </TabPanel>

                      {/* OneDrive Tab */}
                      <TabPanel>
                        <TemplateDownloadBanner />
                        <OneDriveUpload uploadCharts={uploadCharts}>
                          <Flex
                            p={4} border="2px dashed" borderColor="#A0AEC0" borderRadius="12px"
                            bgColor={'white'} flexDirection="column" alignItems="center" justifyContent="center"
                            _hover={{ borderColor: '#48BB78', bgColor: '#E6FFFA' }}
                            style={{ cursor: 'pointer' }} mb={4}
                          >
                            <Text fontSize="16px" fontWeight="bold" color="#2D3748" mb={4}>Upload from OneDrive</Text>
                            <MsOneDriveIcon />
                            <Text fontSize="12px" color="#A0AEC0" mt={4}>Supported formats: XLS, XLSX, PDF, DOC, DOCX, HL7</Text>
                          </Flex>
                        </OneDriveUpload>
                      </TabPanel>
                    </TabPanels>
                  </Tabs>

                  {/* Uploaded Charts List */}
                  <AnimatePresence>
                    {uploadedCharts?.length ? (
                      <>
                        <PerfectScrollbar style={{ maxHeight: '220px', marginTop: 5, paddingRight: 3 }}>
                          {(isGrouped && groupedUploadedCharts)
                            ? Object.entries(groupedUploadedCharts).map(([key, value], index) => (
                              <>
                                {groupedStatusList[index] ? (
                                  <motion.div
                                    key={key} layout
                                    initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                                    exit={{ opacity: 0, x: 50, height: 0 }} transition={{ duration: 0.6 }}
                                  >
                                    <Box key={key} mt={3} width={"100%"} borderRadius="md" border="2px solid" borderColor="gray.100" boxShadow="0 2px 8px rgba(0, 0, 0, 0.1)">
                                      <Flex width={"100%"}>
                                        <HStack
                                          flex={"8"} onClick={() => toggle(index)} cursor="pointer"
                                          w="100%" _hover={{ bg: "gray.100" }} p={1} borderRadius="md" alignItems="baseline"
                                        >
                                          {indexOpenStatus && (<span>{indexOpenStatus[index] ? "▼" : "▶"}</span>)}
                                          <span style={{ fontWeight: "bold", color: "#444" }}>{key}</span>
                                        </HStack>
                                        <Tooltip placement='right' hasArrow label={"Seperates files from this group only"}>
                                          <Button m={1} size={"sm"} onClick={() => {
                                            setGroupedStatusList(prev => prev.map((val, idx) => idx === index ? false : val));
                                          }}>
                                            Ungroup
                                          </Button>
                                        </Tooltip>
                                        <CloseIcon
                                          boxSize={7} color="red" borderRadius="full" p={1} mt={1.5} cursor="pointer"
                                          _hover={{ bg: "red.500", color: "white" }}
                                          onClick={() => {
                                            handleRemoveGroupChartUploads(value.map(chart => chart.id));
                                            removeGroupStateMetadata(index);
                                          }}
                                        />
                                      </Flex>
                                      <Collapse in={indexOpenStatus && indexOpenStatus[index]} style={{ width: '100%' }}>
                                        <VStack align="start" spacing={1} pl={7}>
                                          {value.map((chart, idx) => ChartDisplayComponent(chart, index, key))}
                                        </VStack>
                                      </Collapse>
                                    </Box>
                                  </motion.div>
                                ) : (value.map((chart, idx) => ChartDisplayComponent(chart, index, key)))}
                              </>
                            ))
                            : uploadedCharts.map(ChartDisplayComponent)
                          }
                        </PerfectScrollbar>

                        <SoftBlockBanner softWarnFiles={softWarnFiles} />
                      </>
                    ) : null}
                  </AnimatePresence>

                  {/* Submit Button */}
                  {uploadedCharts?.length ? (
                    <Box position="relative" mt={4} w="100%">
                      <Center>
                        <HStack>
                          {groupingTooltipButtonComponent()}
                          <Button
                            onClick={ioState.on}
                            bg="#48BB78"
                            color="white"
                            borderRadius={20}
                            px={10}
                            _hover={{ bg: '#38A169', color: "white" }}
                            disabled={
                              !uploadedCharts.every(chart => chart.uploaded) ||
                              validateFileNameLength()
                            }
                          >
                            Proceed
                          </Button>
                        </HStack>
                      </Center>
                      <Button
                        isLoading={!allChartsUploaded}
                        position="absolute" right="0" top="50%" transform="translateY(-50%)"
                        colorScheme="red"
                        onClick={() => bulkDeleteChartUploads(uploadedCharts.map(chart => chart.id))}
                      >
                        Remove all Files
                      </Button>
                    </Box>
                  ) : null}

                  {validateFileNameLength() && (
                    <Center mt={4}>
                      <Text color="red.500" fontSize="sm">* File name exceeds the 200-character limit.</Text>
                    </Center>
                  )}
                </>
              ) : (
                <Center mt={8}>
                  <Text color="red.500" fontSize="lg">You do not have permission to upload files.</Text>
                </Center>
              )}
            </Flex>
          </Center>
        </Box>

        {canUpload && (
          <HStack display={'flex'} justifyContent={'center'} mt={2}>
            <Text fontSize={'14px'} fontFamily={FONT_FAMILY.ptSans}>
              By uploading files, you agree to our{' '}
              <span style={{ fontSize: '14px', fontFamily: FONT_FAMILY.ptSans, fontWeight: 'bold' }}>
                Terms of Service
              </span>
            </Text>
          </HStack>
        )}

        <SubmitModal isOpen={isOpen} onClose={() => ioState.off()} onSubmit={submitCharts} />
      </CQContainer>
    </>
  );
};

export default withProvider(UploadProvider, UploadContainer);